// src/IndexerState.ts
import { EthereumIndexer } from "ethereum-indexer";

// src/utils/stores.ts
import { writable } from "sveltore";
function createStore($state) {
  const store = writable($state);
  function set3(data) {
    for (const field of Object.keys(data)) {
      $state[field] = data[field];
    }
    store.set($state);
  }
  return {
    set: set3,
    $state,
    readable: {
      $state,
      subscribe: store.subscribe,
      acknowledgeError() {
        set3({ error: void 0 });
      }
    }
  };
}
function createRootStore(initialState) {
  let $state = initialState;
  const store = writable($state);
  function set3(newState) {
    $state = newState;
    store.set($state);
  }
  return {
    set: set3,
    get $state() {
      return $state;
    },
    readable: {
      get $state() {
        return $state;
      },
      subscribe: store.subscribe
    }
  };
}

// src/IndexerState.ts
import { useStores } from "use-stores";

// src/utils/format.ts
function formatLastSync(lastSync) {
  return filterOutFieldsFromObject(lastSync, ["_rev", "_id", "batch"]);
}
function filterOutFieldsFromObject(obj, fields) {
  const keys = Object.keys(obj);
  const newObj = {};
  for (const key of keys) {
    if (fields.indexOf(key) !== -1) {
      continue;
    }
    newObj[key] = obj[key];
  }
  return newObj;
}

// src/IndexerState.ts
import { logs } from "named-logs";

// src/utils/time.ts
function wait(seconds) {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1e3));
}

// src/IndexerState.ts
var namedLogger = logs("ethereum-indexer-browser");
function createIndexerState(processor, options) {
  const {
    $state: $syncing,
    set: setSyncing,
    readable: readableSyncing
  } = createStore({
    waitingForProvider: true,
    loading: false,
    autoIndexing: false,
    catchingUp: false,
    fetchingLogs: false,
    processingFetchedLogs: false,
    numRequests: options?.trackNumRequests ? 0 : void 0
  });
  if (options?.keepState) {
    if (!processor.keepState) {
      throw new Error(`this processor do not support "keepState" config`);
    }
    processor.keepState(options.keepState);
  }
  const initialState = processor.createInitialState();
  const { set: setStatus, readable: readableStatus } = createStore({ state: "Idle" });
  const { set: setState, readable: readableState } = createRootStore(initialState);
  let indexer;
  let indexingTimeout;
  let autoIndexingInterval = 4;
  async function init(indexerSetup, processorConfig) {
    const config = { ...{}, keepStream: options?.keepStream, ...indexerSetup.config || {} };
    const source = indexerSetup.source;
    let provider = indexerSetup.provider;
    if (options?.trackNumRequests && !options.logRequests) {
      provider = new Proxy(indexerSetup.provider, {
        get(target, p, receiver) {
          if (p === "request") {
            return (args) => {
              if (options.trackNumRequests) {
                setSyncing({ numRequests: ($syncing.numRequests || 0) + 1 });
              }
              return target[p](args);
            };
          }
          return target[p];
        }
      });
    } else if (options?.logRequests) {
      provider = new Proxy(indexerSetup.provider, {
        get(target, p, receiver) {
          if (p === "request") {
            return async (args) => {
              if (options.trackNumRequests) {
                setSyncing({ numRequests: ($syncing.numRequests || 0) + 1 });
              }
              if (options.logRequests) {
                console.log(JSON.stringify(args));
              }
              let response;
              try {
                response = await target[p](args);
                console.log(`  =>`, JSON.stringify(response));
              } catch (err) {
                console.error(`  error:`, err);
                throw err;
              }
              return response;
            };
          }
          return target[p];
        }
      });
    }
    if (processor.configure && processorConfig) {
      processor.configure(processorConfig);
    }
    indexer = new EthereumIndexer(provider, processor, source, config);
    setSyncing({ waitingForProvider: false });
  }
  let lastLastToBlock;
  function setLastSync(lastSync) {
    if (!lastSync) {
      return;
    }
    if (!indexer) {
      throw new Error(`no indexer`);
    }
    const startingBlock = indexer.defaultFromBlock;
    const latestBlock = lastSync.latestBlock;
    const lastToBlock = lastSync.lastToBlock;
    lastLastToBlock = lastToBlock;
    const totalToProcess = latestBlock - startingBlock;
    const numBlocksProcessedSoFar = Math.max(0, lastToBlock - startingBlock);
    const lastSyncObject = formatLastSync(lastSync);
    lastSyncObject.numBlocksProcessedSoFar = numBlocksProcessedSoFar;
    lastSyncObject.syncPercentage = Math.floor(numBlocksProcessedSoFar * 1e6 / totalToProcess) / 1e4;
    lastSyncObject.totalPercentage = Math.floor(lastToBlock * 1e6 / latestBlock) / 1e4;
    setSyncing({ lastSync: lastSyncObject });
  }
  async function setupIndexing() {
    if ($syncing.lastSync) {
      return $syncing.lastSync;
    }
    if (!indexer) {
      throw new Error(`no indexer`);
    }
    indexer.onLoad = async (loadingState) => {
      if (loadingState === "Loading") {
        setStatus({ state: "Loading" });
      } else if (loadingState === "FetchingEventStream") {
        setSyncing({ fetchingLogs: true });
        setStatus({ state: "FetchingEventStream" });
      } else if (loadingState === "ProcessingEventStream") {
        setSyncing({ fetchingLogs: false, processingFetchedLogs: true });
        setStatus({ state: "ProcessingEventStream" });
      } else if (loadingState === "Loaded") {
        setSyncing({ processingFetchedLogs: false });
        setSyncing({ catchingUp: true });
        setStatus({ state: "CatchingUp" });
      }
      await wait(1e-3);
    };
    indexer.onLastSyncUpdated = (lastSync) => {
      setLastSync(lastSync);
      setCatchup(lastSync);
    };
    indexer.onStateUpdated = (state) => {
      setState(state);
    };
    setSyncing({ loading: true });
    try {
      const lastSync = await indexer.load();
      setSyncing({ loading: false });
      return lastSync;
    } finally {
      setSyncing({ loading: false, error: { message: "Failed to load", id: "FAILED_TO_LOAD" } });
    }
  }
  async function indexMore() {
    await setupIndexing();
    if (!indexer) {
      throw new Error(`no indexer`);
    }
    const lastSync = await indexer.indexMore();
    setLastSync(lastSync);
    setCatchup(lastSync);
    return lastSync;
  }
  async function indexMoreAndCatchupIfNeeded() {
    await setupIndexing();
    if (!indexer) {
      throw new Error(`no indexer`);
    }
    const lastSync = await indexer.indexMore();
    setLastSync(lastSync);
    setCatchup(lastSync);
    if (lastSync.lastToBlock !== lastSync.latestBlock) {
      return indexToLatest();
    }
    return lastSync;
  }
  function setCatchup(lastSync) {
    if (lastSync.latestBlock - lastSync.lastToBlock > (options?.catchupThreshold || 20)) {
      if (!$syncing.catchingUp) {
        setSyncing({ catchingUp: true });
        setStatus({ state: "CatchingUp" });
      }
    } else {
      if ($syncing.catchingUp) {
        setSyncing({ catchingUp: false });
        setStatus({ state: "IndexingLatest" });
      }
    }
  }
  async function indexToLatest() {
    let lastSync = await setupIndexing();
    setLastSync(lastSync);
    setCatchup(lastSync);
    if (!indexer) {
      throw new Error(`no indexer`);
    }
    try {
      lastSync = await indexer.indexMore();
      setLastSync(lastSync);
      setCatchup(lastSync);
    } catch (err) {
      lastSync = await new Promise((resolve) => {
        setTimeout(async () => {
          const result = await indexToLatest();
          resolve(result);
        }, 1e3);
      });
    }
    if (!lastSync) {
      throw new Error(`no lastSync`);
    }
    while (lastSync.lastToBlock !== lastSync.latestBlock) {
      try {
        lastSync = await indexer.indexMore();
        setLastSync(lastSync);
        setCatchup(lastSync);
      } catch (err) {
        await new Promise((resolve) => {
          setTimeout(resolve, 1e3);
        });
      }
    }
    return lastSync;
  }
  async function startAutoIndexing(intervalInSeconds = 4) {
    autoIndexingInterval = intervalInSeconds;
    await setupIndexing();
    if (!$syncing.autoIndexing) {
      _auto_index();
      return true;
    } else {
      return false;
    }
  }
  function stopAutoIndexing() {
    if ($syncing.autoIndexing) {
      if (indexingTimeout) {
        clearTimeout(indexingTimeout);
      }
      setSyncing({
        autoIndexing: false
      });
      return true;
    } else {
      return false;
    }
  }
  function reset() {
    if (!indexer) {
      throw new Error(`no indexer`);
    }
    return indexer.reset();
  }
  async function _auto_index() {
    setSyncing({ autoIndexing: true });
    try {
      const lastSync = await indexMoreAndCatchupIfNeeded();
      if (lastSync.latestBlock - lastSync.lastToBlock < 1) {
        indexingTimeout = setTimeout(_auto_index, autoIndexingInterval * 1e3);
      } else {
        indexingTimeout = setTimeout(_auto_index, 1);
      }
    } catch (err) {
      namedLogger.error("ERROR, retry in 1 seconds", err);
      indexingTimeout = setTimeout(_auto_index, autoIndexingInterval * 1e3);
      return;
    }
  }
  return {
    syncing: {
      subscribe: readableSyncing.subscribe,
      get $state() {
        return readableSyncing.$state;
      }
    },
    state: {
      subscribe: readableState.subscribe,
      get $state() {
        return readableState.$state;
      }
    },
    status: {
      subscribe: readableStatus.subscribe,
      get $state() {
        return readableStatus.$state;
      }
    },
    init,
    indexToLatest,
    indexMore,
    indexMoreAndCatchupIfNeeded,
    startAutoIndexing,
    stopAutoIndexing,
    reset,
    updateProcessor(newProcessor) {
      if (!indexer) {
        throw new Error(`no indexer setup, call init`);
      }
      indexer.updateProcessor(newProcessor);
    },
    updateIndexer(update) {
      if (!indexer) {
        throw new Error(`no indexer setup, call init`);
      }
      indexer.updateIndexer(update);
    },
    withHooks(react) {
      const { useReadable } = useStores(react);
      return {
        ...this,
        useState: () => useReadable(this.state, false),
        useSyncing: () => useReadable(this.syncing, false),
        useStatus: () => useReadable(this.status, false)
      };
    }
  };
}

// src/index.ts
import { simple_hash as simple_hash3 } from "ethereum-indexer";

// src/storage/state/OnIndexedDB.ts
import { simple_hash } from "ethereum-indexer";
import { contextFilenames } from "ethereum-indexer-utils";
import { get, set, del } from "idb-keyval";
function getStorageID(name, chainId, config) {
  const configHash = config ? simple_hash(config) : void 0;
  return `${name}_${chainId}${configHash ? `_${configHash}` : ""}`;
}
function getURL(remote, context, lastSync = false) {
  let url;
  if (typeof remote === "string") {
    url = remote;
  } else if ("url" in remote) {
    url = remote.url;
  } else {
    const { stateFile, lastSyncFile } = contextFilenames(context);
    if (lastSync) {
      url = remote.prefix + lastSyncFile;
    } else {
      url = remote.prefix + stateFile;
    }
  }
  return url;
}
function keepStateOnIndexedDB(name, remote) {
  return {
    fetch: async (context) => {
      const storageID = getStorageID(name, context.source.chainId, "config" in context ? context.config : void 0);
      const existingState = await get(storageID);
      let remoteState;
      if (remote) {
        if (Array.isArray(remote)) {
          let latest;
          for (let i = 0; i < remote.length; i++) {
            if (typeof remote[i] === "string" || "url" in remote[i]) {
              const urlOfRemote = getURL(remote[i], context);
              try {
                const response = await fetch(urlOfRemote);
                const json = await response.json();
                if (!latest || !latest.lastSync || json.lastSync && json.lastSync.lastToBlock > latest.lastSync.lastToBlock) {
                  latest = {
                    index: i,
                    lastSync: json.lastSync
                  };
                }
              } catch (err) {
                console.error(`failed to fetch remote lastSync`, err);
              }
            } else {
              const urlOfLastSync = getURL(remote[i], context, true);
              try {
                const response = await fetch(urlOfLastSync);
                const json = await response.json();
                if (!latest || !latest.lastSync || json.lastToBlock > latest.lastSync.lastToBlock) {
                  latest = {
                    index: i,
                    lastSync: json
                  };
                }
              } catch (err) {
                console.error(`failed to fetch remote lastSync`, err);
              }
            }
          }
          if (existingState && latest && latest.lastSync && latest.lastSync.lastToBlock < existingState.lastSync.lastToBlock) {
            return existingState;
          }
          if (!latest) {
            console.error(`could not fetch any valid lastSync, still continue with first`);
            latest = {
              index: 0
            };
          }
          const url = getURL(remote[latest.index], context);
          try {
            const response = await fetch(url);
            const json = await response.json();
            remoteState = json;
          } catch (err) {
            console.error(`failed to fetch remote-state, try second`, err);
            const url2 = getURL(remote[(latest.index + 1) % remote.length], context);
            try {
              const response = await fetch(url2);
              const json = await response.json();
              remoteState = json;
            } catch (err2) {
              console.error(`failed to fetch second remote-state`, err2);
            }
          }
        } else {
          const url = getURL(remote, context);
          try {
            const response = await fetch(url);
            const json = await response.json();
            remoteState = json;
          } catch (err) {
            console.error(`failed to fetch remote-state`, err);
          }
        }
      }
      if (!existingState) {
        return remoteState;
      } else {
        if (remoteState && remoteState.lastSync.lastToBlock >= existingState.lastSync.lastToBlock) {
          return remoteState;
        }
        return existingState;
      }
    },
    save: async (context, all) => {
      const storageID = getStorageID(name, context.source.chainId, "config" in context ? context.config : void 0);
      await set(storageID, { ...all, __VERSION__: context.version });
    },
    clear: async (context) => {
      const storageID = getStorageID(name, context.source.chainId, "config" in context ? context.config : void 0);
      await del(storageID);
    }
  };
}

// src/storage/state/OnLocalStorage.ts
import { simple_hash as simple_hash2 } from "ethereum-indexer";
function getStorageID2(name, chainId, config) {
  const configHash = config ? simple_hash2(config) : void 0;
  return `${name}_${chainId}${configHash ? `_${configHash}` : ""}`;
}
function keepStateOnLocalStorage(name) {
  return {
    fetch: async (context) => {
      const storageID = getStorageID2(name, context.source.chainId, "config" in context ? context.config : void 0);
      const fromStorage = localStorage.getItem(storageID);
      if (!fromStorage) {
        return void 0;
      } else {
        const parsed = JSON.parse(fromStorage, (_, value) => {
          if (typeof value === "string" && value.endsWith("n")) {
            try {
              const bn = BigInt(value.slice(0, -1));
              return bn;
            } catch (err) {
              return value;
            }
          } else {
            return value;
          }
        });
        return parsed;
      }
    },
    save: async (context, all) => {
      const storageID = getStorageID2(name, context.source.chainId, "config" in context ? context.config : void 0);
      localStorage.setItem(
        storageID,
        JSON.stringify(
          { ...all, __VERSION__: context.version },
          (_, value) => typeof value === "bigint" ? value.toString() + "n" : value
        )
      );
    },
    clear: async (context) => {
      const storageID = getStorageID2(name, context.source.chainId, "config" in context ? context.config : void 0);
      localStorage.removeItem(storageID);
    }
  };
}

// src/storage/stream/OnIndexedDB.ts
import { get as get2, set as set2, del as del2 } from "idb-keyval";
function keepStreamOnIndexedDB(name) {
  return {
    fetchFrom: async (source, fromBlock) => {
      const storageID = `stream_${name}_${source.chainId}`;
      const existingStream = await get2(storageID);
      return existingStream ? {
        eventStream: existingStream.eventStream.filter((v) => v.blockNumber >= fromBlock),
        lastSync: existingStream.lastSync
      } : void 0;
    },
    saveNewEvents: async (source, stream) => {
      const storageID = `stream_${name}_${source.chainId}`;
      const existingStream = await get2(storageID);
      if (existingStream && existingStream.eventStream.length > 0) {
        if (stream.eventStream.length > 0) {
          const eventStreamToSave = existingStream.eventStream.concat(stream.eventStream);
          await set2(storageID, { lastSync: stream.lastSync, eventStream: eventStreamToSave });
        } else {
          await set2(storageID, { lastSync: stream.lastSync, eventStream: existingStream.eventStream });
        }
      } else {
        await set2(storageID, stream);
      }
    },
    async clear(source) {
      const storageID = `stream_${name}_${source.chainId}`;
      await del2(storageID, void 0);
    }
  };
}
export {
  createIndexerState,
  keepStateOnIndexedDB,
  keepStateOnLocalStorage,
  keepStreamOnIndexedDB,
  simple_hash3 as simple_hash
};
